/**
 * React Query hooks for platform portal.
 * Product-specific API calls have been removed — each product now has its own frontend.
 * Only billing/subscription hooks remain here.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { billingApi } from '@/api/client';
import type { AutoRechargeUpdate } from '@/types';

// ─── Billing ───────────────────────────────────────────────

const BILLING_STALE_MS = 5 * 60 * 1000;

export function useSubscriptions() {
  return useQuery({
    queryKey: ['subscriptions'],
    queryFn: async () => {
      const { data } = await billingApi.subscriptions();
      return data;
    },
    staleTime: BILLING_STALE_MS,
    retry: 2,
  });
}

export function useInvoices() {
  return useQuery({
    queryKey: ['invoices'],
    queryFn: async () => {
      const { data } = await billingApi.invoices();
      return data;
    },
    staleTime: BILLING_STALE_MS,
    retry: 2,
  });
}

export function useCheckout() {
  return useMutation({
    mutationFn: async (data: { productSlug: string; plan: string }) => {
      const res = await billingApi.checkout(data);
      return res.data;
    },
  });
}

export function useCustomerPortal() {
  return useMutation({
    mutationFn: async () => {
      const res = await billingApi.portal();
      return res.data;
    },
  });
}

export function useCredits() {
  return useQuery({
    queryKey: ['billing', 'credits'],
    queryFn: async () => {
      const { data } = await billingApi.credits();
      return data;
    },
    staleTime: BILLING_STALE_MS,
    retry: 2,
  });
}

export function usePaymentMethod() {
  return useQuery({
    queryKey: ['billing', 'payment-method'],
    queryFn: async () => {
      const { data } = await billingApi.paymentMethod();
      return data;
    },
    staleTime: BILLING_STALE_MS,
    retry: 2,
  });
}

/** Purchase a one-off credit pack; resolves to the Stripe Checkout URL. */
export function useRefillCredits() {
  return useMutation({
    mutationFn: async (creditPack: number) => {
      const res = await billingApi.refill(creditPack);
      return res.data;
    },
  });
}

// ─── Cartes ────────────────────────────────────────────────
//
// Les trois mutations posent elles-mêmes la liste renvoyée dans le cache plutôt
// que d'invalider : la réponse EST la liste après le geste, et une invalidation
// ferait clignoter le panneau le temps d'un aller-retour pour réafficher ce
// qu'on tient déjà. `setup` fait exception — c'est une navigation, il n'y a rien
// à remettre en cache.

/** Start the hosted card capture; resolves to the Stripe Checkout URL. */
export function useSetupPaymentMethod() {
  return useMutation({
    mutationFn: async () => {
      const res = await billingApi.setupPaymentMethod();
      return res.data;
    },
  });
}

export function useSetDefaultPaymentMethod() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (paymentMethodId: string) => {
      const res = await billingApi.setDefaultPaymentMethod(paymentMethodId);
      return res.data;
    },
    onSuccess: (data) => queryClient.setQueryData(['billing', 'payment-method'], data),
  });
}

export function useRemovePaymentMethod() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (paymentMethodId: string) => {
      const res = await billingApi.removePaymentMethod(paymentMethodId);
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['billing', 'payment-method'], data);
      // Removing the last card disarms auto-recharge server-side, so the panel's
      // other card must be refetched or it would keep claiming to be armed.
      if (data.autoRechargeDisabled) {
        void queryClient.invalidateQueries({ queryKey: ['billing', 'auto-recharge'] });
      }
    },
  });
}

// ─── Rechargement automatique ──────────────────────────────

export function useAutoRecharge() {
  return useQuery({
    queryKey: ['billing', 'auto-recharge'],
    queryFn: async () => {
      const { data } = await billingApi.autoRecharge();
      return data;
    },
    staleTime: BILLING_STALE_MS,
    retry: 2,
  });
}

export function useUpdateAutoRecharge() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: AutoRechargeUpdate) => {
      const res = await billingApi.updateAutoRecharge(body);
      return res.data;
    },
    onSuccess: (data) => queryClient.setQueryData(['billing', 'auto-recharge'], data),
  });
}
